import { Pipe, PipeTransform } from '@angular/core';
import { QeqService } from '../../services/qeq/qeq.service';
import { QeqUser } from '../../interfaces/qeq.interface';
import { TokenExpirationService } from '../../services/token-expiration/token-expiration.service';

@Pipe({
  name: 'qeqPipe',
  pure: false, // Make the pipe impure so it re-evaluates when data changes
})
export class QeqPipe implements PipeTransform {
  private static pendingRequests = new Set<string>();
  private static lastErrorTimestamp: number = 0;
  private static errorCount: number = 0;

  // Cooldown period in milliseconds (30 seconds)
  private static readonly ERROR_COOLDOWN_MS = 30000;
  // Maximum retries before giving up completely
  private static readonly MAX_RETRIES = 3;

  constructor(
    private qeqService: QeqService,
    private tokenExpirationService: TokenExpirationService
  ) {}

  transform(value: string | Date | undefined): string {
    if (!value) return '';

    const externalId = typeof value === 'string' ? value.trim() : '';

    // Get cached QeQ data synchronously
    const cached = this.qeqService.getQeqFromStorage();

    // Check if data exists in cache
    if (cached?.data && Array.isArray(cached.data)) {
      const user = cached.data.find(
        (u: any) => u.personnelnumber === externalId
      );
      return user?.name || externalId;
    }

    // Check if we've exceeded max retries
    if (QeqPipe.errorCount >= QeqPipe.MAX_RETRIES) {
      return externalId;
    }

    // Check if we're still in cooldown period after an error
    const now = Date.now();
    if (
      QeqPipe.lastErrorTimestamp > 0 &&
      now - QeqPipe.lastErrorTimestamp < QeqPipe.ERROR_COOLDOWN_MS
    ) {
      return externalId;
    }

    // If no cached data and not already loading globally, trigger API call
    const REQUEST_KEY = 'qeq-data';
    if (!QeqPipe.pendingRequests.has(REQUEST_KEY)) {
      QeqPipe.pendingRequests.add(REQUEST_KEY);

      this.qeqService.getQeQ().subscribe({
        next: (response) => {
          QeqPipe.pendingRequests.delete(REQUEST_KEY);

          // Check if response contains valid data array
          if (response?.data && Array.isArray(response.data)) {
            // Reset error state on success
            QeqPipe.lastErrorTimestamp = 0;
            QeqPipe.errorCount = 0;
          } else {
            // API returned 200 but with invalid/error response body
            QeqPipe.lastErrorTimestamp = Date.now();
            QeqPipe.errorCount++;
            console.error(
              `QeQ API returned invalid data (attempt ${QeqPipe.errorCount}/${QeqPipe.MAX_RETRIES}):`,
              response
            );
          }
          // The pipe will re-evaluate automatically due to being impure
        },
        error: (error) => {
          QeqPipe.pendingRequests.delete(REQUEST_KEY);
          // Track error state to prevent infinite retries
          QeqPipe.lastErrorTimestamp = Date.now();
          QeqPipe.errorCount++;
          console.error(
            `Error fetching QeQ data in pipe (attempt ${QeqPipe.errorCount}/${QeqPipe.MAX_RETRIES}):`,
            error
          );

          // Handle 401 responses (token expired)
          if (error.status === 401 || error.status_code === 401) {
            console.warn(
              'Token expired while fetching QeQ data, handling expiration'
            );
            this.tokenExpirationService.handleTokenExpiration();
          }
        },
      });
    }

    // Return the original value while loading or if no data
    return externalId;
  }
}
